// Mirrors backend/agent/transport.py AgentResponseKind. Underscore form per D-21 (Phase 2).
export type AgentResponseKind =
  | 'text_chunk'
  | 'tool_call'
  | 'tool_result'
  | 'ui_flow'
  | 'price_warning'
  | 'pix_qr'
  | 'lead_captured'
  | 'quick_replies'
  | 'done';

/**
 * Dados de uma cotação magistral retornados pelo backend (evento `cotacao`).
 * O conteúdo (preço, faixa, breakdown, modelo_usado, etc.) é definido pelo
 * pricer e tratado como opaco pela UI — preservamos o objeto inteiro em
 * `dados` e exibimos os campos conhecidos quando presentes.
 */
export type Cotacao = {
  preco?: number;
  /** Faixa de preço (banda do modelo), quando disponível. */
  faixa?: [number, number] | { min: number; max: number };
  dados: Record<string, unknown>;
};

/**
 * Um plano da oferta alternativa (evento `oferta_alt`). `delta_pct` é o prêmio
 * (%) do preço vs o plano-cápsula equivalente (positivo = mais caro).
 */
export type PlanoAlt = { dias: number; n_unidades?: number; preco: number; delta_pct?: number };

/**
 * Oferta anexa (sublingual) exibida SOB o CotacaoCard. O orçamento principal
 * (cápsula) não muda; esta é uma alternativa opcional precificada pelo motor.
 */
export type OfertaAlt = {
  tipo: 'sublingual';
  titulo: string;
  subtitulo: string;
  planos: PlanoAlt[];
};

// Wire-format event union despachado para o store.
// Os eventos do nosso backend ({tipo: token|cotacao|done}) são traduzidos em
// `sse.ts` para estas formas antes de chegarem ao hook.
export type AgentEvent =
  | { kind: 'text_chunk'; text: string; done: false }
  | { kind: 'tool_call'; name: string; args: Record<string, unknown>; text: ''; done: false }
  | { kind: 'tool_result'; name: string; result: unknown; text: ''; done: false }
  | { kind: 'ui_flow'; payload: UiFlowPayload; text: ''; done: false }
  | { kind: 'price_warning'; warning: string; text: ''; done: false }
  | { kind: 'pix_qr'; payload: PixPayload; text: ''; done: false }
  | { kind: 'lead_captured'; payload: Record<string, unknown>; text: ''; done: false }
  | { kind: 'quick_replies'; labels: string[]; text: ''; done: false }
  | { kind: 'cotacao'; cotacao: Cotacao; text: ''; done: false }
  | { kind: 'oferta_alt'; oferta: OfertaAlt; text: ''; done: false }
  | { kind: 'done'; text: ''; done: true };

export type DeliveryState = 'sending' | 'delivered' | 'read';
export type ConnectionState = 'idle' | 'connected' | 'streaming' | 'reconnecting' | 'error';

/**
 * Payload de envio que o frontend POSTa em `${VITE_API_BASE}/chat`.
 * - `texto`: conteudo = a mensagem digitada.
 * - `imagem`: conteudo = data-URL base64 da imagem (ex.: "data:image/png;base64,...").
 * - `audio`: conteudo = base64 do áudio; `mime` indica o formato gravado/anexado.
 * - `pdf`: conteudo = data-URL base64 do PDF; `mime` = "application/pdf".
 *
 * `nome` é o nome do arquivo (ex.: receita.pdf) — usado só na UI (chip na bolha);
 * o transporte (sse.ts) NÃO o envia ao backend.
 */
export type OutgoingPayload = {
  tipo: 'texto' | 'imagem' | 'audio' | 'pdf';
  conteudo: string;
  mime?: string;
  nome?: string;
};

export type PixPayload = {
  qr_base64: string;
  pix_key: string;
  amount: number;
};

/** A quick-reply button. `label` is displayed; `value` is sent as the message. */
export type QuickReply = { label: string; value: string };

export type Message = {
  id: string;
  role: 'user' | 'assistant' | 'tool_status';
  text: string;
  timestamp: number;
  deliveryState: DeliveryState;
  uiPayload?: UiFlowPayload;
  priceWarning?: string;
  pixPayload?: PixPayload;
  /**
   * Quick-reply buttons attached to this bubble.
   * - Onboarding: set on the welcome message when no user messages exist yet.
   * - Agent-driven: set by attachQuickReplies(); also shown in the keyboard strip.
   * Cleared on next user send.
   */
  quickReplies?: QuickReply[];
  /** Cotação magistral anexada a esta bolha (evento `cotacao` do backend). */
  cotacao?: Cotacao;
  /**
   * Todas as cotações desta bolha, na ordem de chegada — populado quando a receita
   * tem MÚLTIPLAS fórmulas (2+ eventos `cotacao` no mesmo turno). `cotacao` acima
   * continua apontando pra ÚLTIMA (retrocompat); use `cotacoes` pra somar o total
   * combinado. Ausente/undefined = fluxo de fórmula única, sem mudança de comportamento.
   */
  cotacoes?: Cotacao[];
  /** Alternativa sublingual anexada sob o CotacaoCard (evento `oferta_alt`). */
  ofertaAlt?: OfertaAlt;
  /**
   * Payload de envio multimodal (imagem/áudio) associado a esta bolha do usuário.
   * Quando ausente, a mensagem é texto puro: o envio usa {tipo:'texto', conteudo: text}.
   */
  outgoing?: OutgoingPayload;
  /** Data-URL da imagem anexada — renderizada como preview na bolha do usuário. */
  imageDataUrl?: string;
  /** True quando a bolha do usuário representa um áudio enviado. */
  isAudio?: boolean;
  /** Nome do PDF anexado — renderizado como chip "📄 <nome>" na bolha do usuário. */
  pdfName?: string;
};

export type SuggestionItem = {
  id: string;
  name: string;
  price: number;
  description?: string | null;
  image_url?: string | null;
  category?: string;
};

export type UiFlowPayload =
  | {
      flow_type: 'menu';
      categories: Array<{
        id: string;
        name: string;
        count: number;
        items: Array<{ id: string; name: string; price: number; description?: string; image_url?: string }>;
      }>;
    }
  | {
      flow_type: 'event_visit';
      /** ISO-8601 date strings for the selectable visit slots (Fri/Sat/Sun). */
      available_dates?: string[];
    }
  | { flow_type: 'address' }
  | {
      flow_type: 'instagram_news';
      posts: Array<{
        id: string;
        caption: string | null;
        image_url: string | null;
        posted_at: string | null;
      }>;
    }
  | {
      /** Visual product cards for pairing / upsell suggestions. Renders inline in the bubble. */
      flow_type: 'suggestions';
      items: SuggestionItem[];
    };

// Locked from D-03 + UI-SPEC "Tool Status Phrases" — used by Zustand store setToolStatus.
export const TOOL_PHRASES: Record<string, string> = {
  search_menu: 'Consultando o cardápio...',
  get_dish_details: 'Buscando o prato...',
  get_pairings: 'Pensando em harmonizações...',
  recommend_for_restriction: 'Buscando opções para você...',
  set_restriction: 'Anotando suas preferências...',
  clear_restriction: 'Anotando suas preferências...',
  get_instagram_news: 'Vendo as novidades...',
  show_menu_flow: 'Abrindo o cardápio digital...',
  add_to_cart: 'Adicionando ao carrinho...',
  remove_from_cart: 'Removendo do carrinho...',
  view_cart: 'Verificando seu carrinho...',
  checkout: 'Processando seu pedido...',
  request_address_flow: 'Preparando formulário de endereço...',
  request_event_visit_flow: 'Abrindo o formulário de visita...',
  schedule_event_visit: 'Registrando sua visita...',
};
export const TOOL_PHRASE_FALLBACK = 'Consultando...';
